/**
 * Where the trader's details sit on each certificate template.
 *
 * A certificate is not an image the backend renders — it is one of eight
 * artwork templates with the name, the date and (on the two payout ones) the
 * amount drawn over it in the right place, in the right script. The old
 * Achievements page did this with a separate component and stylesheet per type;
 * this is the same eight layouts as data, since the only thing that differed
 * between those components was the coordinates.
 *
 * `type` is the API's own field on each row of
 * `GET /v2/achievements/certificates/list/`.
 *
 * NUMBERS
 * Every position is a percentage of the artwork, and every size and width is a
 * percentage of its *width* (`cqw` — see CertificateArt.scss). The old code
 * wrote each layout twice, once for the thumbnail in `vw` and once for the
 * 720px modal in `clamp()`, which is why the two drifted apart by a percent
 * here and there. These are the modal numbers — the ones that got downloaded —
 * divided by that 720px, so one set now drives the certificate at every size.
 *
 * All eight templates are 4:3 (1968×1487 and 1080×816 both reduce to 1.3235),
 * which is what makes a single ratio in the stylesheet correct for all of them.
 */

const CLASH = "'Clash Display', Inter, sans-serif";
const RESOLVE = "'Resolve Sans Light', Inter, sans-serif";
const ROBOTO = "Roboto, Inter, sans-serif";
const ALEX_BRUSH = "'Alex Brush', cursive";
const ALLURA = "Allura, cursive";
const BIRTHSTONE = "Birthstone, cursive";

/** The two off-white inks the artwork is signed in. */
const WHITE = '#f6f6f6';
const INK = '#0e1011';

export const CERTIFICATE_TEMPLATES = {
  'Stage 1 Pass': {
    image: '/certificates/templates/stage-1.jpg',
    fields: [
      { key: 'name', top: 47, left: 64, width: 34.72, size: 3.194, font: CLASH, color: WHITE },
      { key: 'date', bottom: 13, left: 53, size: 2.778, font: ALEX_BRUSH, color: WHITE },
    ],
  },

  'Stage 2 Pass': {
    image: '/certificates/templates/stage-2.jpg',
    fields: [
      { key: 'name', top: 47.7, left: 64, width: 42.92, size: 3.472, font: RESOLVE, color: INK },
      { key: 'date', bottom: 12, left: 53, size: 4.167, font: BIRTHSTONE, color: INK },
    ],
  },

  'Stage 3 Pass': {
    image: '/certificates/templates/stage-3.jpg',
    fields: [
      { key: 'name', top: 46.9, left: 64, width: 42.92, size: 3.472, font: RESOLVE, color: '#fff' },
      { key: 'date', bottom: 12, left: 53, size: 4.167, font: BIRTHSTONE, color: '#fff' },
    ],
  },

  Qualified: {
    image: '/certificates/templates/qualified.jpg',
    fields: [
      { key: 'name', top: 46.9, left: 71, width: 34.72, size: 3.056, font: RESOLVE, color: WHITE },
      { key: 'date', bottom: 12, left: 58, size: 3.75, font: BIRTHSTONE, color: WHITE },
    ],
  },

  'Funded Profit': {
    image: '/certificates/templates/funded-profit.jpg',
    fields: [
      { key: 'name', top: 43.3, left: 64, width: 34.72, size: 3.194, font: CLASH, color: WHITE },
      { key: 'date', bottom: 13, left: 53, size: 2.778, font: ALEX_BRUSH, color: WHITE },
    ],
  },

  'Max Allocation': {
    image: '/certificates/templates/max-allocation.jpg',
    fields: [
      { key: 'name', top: 44, left: 50, width: 36.53, size: 3.333, font: ROBOTO, color: WHITE },
      { key: 'date', bottom: 11, left: 24, size: 2.778, font: ALLURA, color: WHITE },
    ],
  },

  // The two that carry a figure. Their amount has no font of its own in the old
  // stylesheets — it inherited the page's body font, so it inherits ours.
  'Profit Split': {
    image: '/certificates/templates/profit-split.jpg',
    fields: [
      { key: 'amount', top: 48, left: 64, width: 42.64, size: 6.389, weight: 700, color: '#fff' },
      { key: 'name', bottom: 30.5, left: 64, width: 43.33, size: 3.333, font: ROBOTO, color: '#fff' },
      { key: 'date', bottom: 11, left: 51.5, size: 3.75, font: ALLURA, color: '#fff' },
    ],
  },

  'Lifetime Payout': {
    image: '/certificates/templates/lifetime-payout.jpg',
    fields: [
      { key: 'amount', top: 58, left: 35, width: 41.67, size: 7.778, weight: 700, color: '#fff' },
      { key: 'name', bottom: 54, left: 35, width: 38.89, size: 2.917, font: ROBOTO, color: '#fff' },
      { key: 'date', bottom: 13, left: 22, size: 3.75, font: ALLURA, color: '#fff' },
    ],
  },
};

/**
 * Long names overflow the fixed-width box the artwork leaves for them, so they
 * are cut to the first words that fill it — ported from the old
 * `getTruncatedName`, which only the Funded template used. It is the same box
 * on every template, so every template gets it; under 23 characters it does
 * nothing at all.
 */
/** The same certificate once the API renames the type to Alpha's word. */
CERTIFICATE_TEMPLATES['Qualified Profit'] = CERTIFICATE_TEMPLATES['Funded Profit'];

export const truncateName = (name = '') => {
  if (!name) return '';
  if (name.length <= 22) return name;

  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    const twoWords = words.slice(0, 2).join(' ');
    if (twoWords.length > 12) return twoWords;
  }
  return words.slice(0, 3).join(' ');
};
